import { useState, useEffect } from 'react'
import { MessageSquare, Send, X, Paperclip, ArrowLeft, Edit, Trash2, Image as ImageIcon } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Textarea } from './ui/textarea'
import { Badge } from './ui/badge'
import { MoonLoader } from './ui/moon-loader'
import { useAuth } from '../contexts/auth-context'
import { apiService } from '../lib/api'
import { config } from '../lib/config'
import { useToast } from '../hooks/use-toast'

// Helper function to extract image URL and clean text for preview
const extractImageAndText = (messageText: string | null | undefined) => {
  if (!messageText) return { hasImage: false, text: '', imageUrl: null }
  
  // Check for MongoDB GridFS image URL pattern
  const patterns = [
    /\/api\/images\/([a-fA-F0-9]{24})/,
    /\/api\/images\/([a-fA-F0-9]+)/,
    /api\/images\/([a-fA-F0-9]{24})/,
    /api\/images\/([a-fA-F0-9]+)/,
  ]
  
  let gridFsMatch = null
  for (const pattern of patterns) {
    gridFsMatch = messageText.match(pattern)
    if (gridFsMatch) break
  }
  
  // Check for base64 image
  const base64Match = messageText.match(/\[IMAGE:([\s\S]*?)\]/)
  
  if (gridFsMatch || base64Match) {
    // Remove image URL/tag from text
    let cleanedText = messageText
      .replace(/\s*\/api\/images\/[a-fA-F0-9]+\s*/g, '')
      .replace(/^\s*\/api\/images\/[a-fA-F0-9]+\s*$/gm, '')
      .replace(/\s*api\/images\/[a-fA-F0-9]+\s*/g, '')
      .replace(/\[IMAGE:[\s\S]*?\]/, '')
      .replace(/\n\n+/g, '\n')
      .replace(/^\n+|\n+$/g, '')
      .trim()
    
    return { hasImage: true, text: cleanedText, imageUrl: null }
  }
  
  return { hasImage: false, text: messageText, imageUrl: null }
}

export function GlobalMessages() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [showMessages, setShowMessages] = useState(false)
  const [messages, setMessages] = useState<any[]>([])
  const [guestsWithMessages, setGuestsWithMessages] = useState<any[]>([])
  const [selectedGuestForChat, setSelectedGuestForChat] = useState<string | null>(null)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingGuestsWithMessages, setLoadingGuestsWithMessages] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [currentGuestId, setCurrentGuestId] = useState<string | null>(null)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingMessageText, setEditingMessageText] = useState('')
  const [editingMessageImageUrl, setEditingMessageImageUrl] = useState<string | null>(null)
  const [editingMessageImagePath, setEditingMessageImagePath] = useState<string | null>(null)
  const [editingNewImage, setEditingNewImage] = useState<File | null>(null)
  const [editingNewImagePreview, setEditingNewImagePreview] = useState<string | null>(null)
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)

  // Check if user is admin/receptionist
  const isAdminOrReceptionist = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'receptionist'

  // Load messages for guest
  const loadMessages = async () => {
    if (!user?.email) return
    
    setLoadingMessages(true)
    try {
      const messagesData = await apiService.getGuestMessages(undefined)
      setMessages(messagesData || [])
      
      // Extract guestId from messages if we don't have it yet
      let guestId = currentGuestId
      if (!guestId && messagesData && messagesData.length > 0) {
        const firstMessage = messagesData[0]
        if (firstMessage.guest?.id) {
          guestId = firstMessage.guest.id
          setCurrentGuestId(guestId)
        }
      }
      
      // Count unread messages (messages from admin/receptionist that are not read)
      const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
      setUnreadMessageCount(unreadCount)
      
      // Mark all messages as read ONLY when guest views them (popup is open)
      if (guestId && showMessages && unreadCount > 0) {
        try {
          await apiService.markAllMessagesAsRead(guestId)
          // Update unread count to 0 after marking as read
          setUnreadMessageCount(0)
        } catch (error) {
          // Silently fail - marking as read is not critical
          console.error('Failed to mark messages as read:', error)
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load messages',
        variant: 'destructive',
      })
    } finally {
      setLoadingMessages(false)
    }
  }

  // Load guests with messages (for admin/receptionist)
  const loadGuestsWithMessages = async () => {
    setLoadingGuestsWithMessages(true)
    try {
      const guests = await apiService.getGuestsWithMessages()
      // Deduplicate guests by ID to prevent duplicates
      const uniqueGuests = Array.from(
        new Map((guests || []).map(guest => [guest.id, guest])).values()
      )
      setGuestsWithMessages(uniqueGuests)
      
      // Calculate total unread count from unique guests only
      const totalUnread = uniqueGuests.reduce((sum: number, guest: any) => sum + (guest.unreadCount || 0), 0)
      setUnreadMessageCount(totalUnread)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load messages',
        variant: 'destructive',
      })
    } finally {
      setLoadingGuestsWithMessages(false)
    }
  }

  // Load messages for a specific guest (for admin/receptionist)
  const loadChatMessages = async (guestId: string) => {
    setLoadingMessages(true)
    try {
      const messagesData = await apiService.getGuestMessages({ guestId })
      setMessages(messagesData || [])
      
      // Mark all messages from this guest as read when admin views them
      try {
        await apiService.markAllMessagesAsRead(guestId)
      } catch (error) {
        // Silently fail - marking as read is not critical
        console.error('Failed to mark messages as read:', error)
      }
      
      // Reload guests list to update unread counts (this also updates the total unread count)
      await loadGuestsWithMessages()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load chat messages',
        variant: 'destructive',
      })
    } finally {
      setLoadingMessages(false)
    }
  }

  // Load messages when component mounts or user changes
  useEffect(() => {
    if (user && showMessages) {
      if (isAdminOrReceptionist) {
        loadGuestsWithMessages()
      } else {
        loadMessages()
      }
    }
  }, [user, showMessages, isAdminOrReceptionist])

  // Also load guests with messages on mount for admin/receptionist (to get unread count)
  useEffect(() => {
    if (user && isAdminOrReceptionist && !showMessages) {
      // Load guests with messages to get unread count for the badge
      const loadUnreadCount = async () => {
        try {
          const guests = await apiService.getGuestsWithMessages()
          const totalUnread = (guests || []).reduce((sum: number, guest: any) => sum + (guest.unreadCount || 0), 0)
          setUnreadMessageCount(totalUnread)
        } catch (error) {
          // Silently fail for unread count
        }
      }
      loadUnreadCount()
    }
  }, [user, isAdminOrReceptionist])

  // Load guests when admin/receptionist selects a guest
  useEffect(() => {
    if (isAdminOrReceptionist && selectedGuestForChat && showMessages) {
      loadChatMessages(selectedGuestForChat)
    }
  }, [selectedGuestForChat, isAdminOrReceptionist, showMessages])

  // Load unread count periodically
  useEffect(() => {
    if (!user) return
    
    const loadUnreadCount = async () => {
      try {
        if (isAdminOrReceptionist) {
          // For admin/receptionist: get unread count from guests with messages
          const guests = await apiService.getGuestsWithMessages()
          const totalUnread = (guests || []).reduce((sum: number, guest: any) => sum + (guest.unreadCount || 0), 0)
          setUnreadMessageCount(totalUnread)
        } else {
          // For guests: get unread count from their messages
          if (currentGuestId) {
            const messagesData = await apiService.getGuestMessages({ guestId: currentGuestId })
            const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
            setUnreadMessageCount(unreadCount)
          } else {
            // Try to load messages to get guestId and check unread count
            const messagesData = await apiService.getGuestMessages(undefined)
            const unreadCount = messagesData ? messagesData.filter((m: any) => !m.isFromGuest && !m.isRead).length : 0
            setUnreadMessageCount(unreadCount)
          }
        }
      } catch (error) {
        // Silently fail for periodic updates
      }
    }
    
    loadUnreadCount()
    const interval = setInterval(loadUnreadCount, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [user, isAdminOrReceptionist, currentGuestId])

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle image removal
  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
  }

  // Convert image to base64
  const imageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Send a message
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedImage) || sendingMessage) return
    
    setSendingMessage(true)
    try {
      let messageText = newMessage.trim()
      
      // If image is selected, convert to base64 and append to message
      if (selectedImage) {
        try {
          const base64Image = await imageToBase64(selectedImage)
          if (messageText) {
            messageText = `${messageText}\n[IMAGE:${base64Image}]`
          } else {
            messageText = `[IMAGE:${base64Image}]`
          }
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to process image',
            variant: 'destructive',
          })
          setSendingMessage(false)
          return
        }
      }
      
      // Get companyId from localStorage (optional - backend will handle if not provided)
      const companyId = localStorage.getItem('company_id') ||
                        localStorage.getItem('companyId') ||
                        localStorage.getItem('company') ||
                        undefined
      
      const response = await apiService.createGuestMessage({
        guestId: isAdminOrReceptionist ? selectedGuestForChat! : (currentGuestId || undefined),
        messageText: messageText,
        isFromGuest: !isAdminOrReceptionist,
        companyId: companyId,
      })
      
      // Update guestId from response if we don't have it yet (for guests only)
      if (!isAdminOrReceptionist && !currentGuestId && response?.guest?.id) {
        setCurrentGuestId(response.guest.id)
      }
      
      setNewMessage('')
      setSelectedImage(null)
      setImagePreview(null)
      
      // Reload messages and update unread counts
      if (isAdminOrReceptionist && selectedGuestForChat) {
        await loadChatMessages(selectedGuestForChat)
      } else {
        await loadMessages()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      })
    } finally {
      setSendingMessage(false)
    }
  }

  // Handle edit message
  const handleEditMessage = (message: any) => {
    setEditingMessageId(message.id)
    
    // Extract text and image
    let imageUrl: string | null = null
    let imagePath: string | null = null
    let textWithoutImage = message.messageText || ''
    
    if (message.messageText) {
      // First, check for MongoDB GridFS image URL pattern: /api/images/{fileId}
      const patterns = [
        /\/api\/images\/([a-fA-F0-9]{24})/,  // Standard format with 24 hex chars
        /\/api\/images\/([a-fA-F0-9]+)/,     // Any hex length with leading slash
        /api\/images\/([a-fA-F0-9]{24})/,    // Without leading slash, 24 chars
        /api\/images\/([a-fA-F0-9]+)/,       // Without leading slash, any length
      ]
      
      let gridFsMatch = null
      for (const pattern of patterns) {
        gridFsMatch = message.messageText.match(pattern)
        if (gridFsMatch) break
      }
      
      if (gridFsMatch) {
        // Construct full URL
        let path = gridFsMatch[0]
        if (!path.startsWith('/')) {
          path = '/' + path
        }
        imagePath = path
        
        // Get baseUrl from apiService
        const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
        imageUrl = path.startsWith('http') 
          ? path 
          : path.startsWith('/')
          ? `${baseUrl}${path}`
          : `${baseUrl}/${path}`
        
        // Remove the image URL from text
        const escapedMatch = gridFsMatch[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        textWithoutImage = message.messageText
          .replace(new RegExp(`\\s*${escapedMatch}\\s*`, 'g'), '')
          .replace(new RegExp(`^\\s*${escapedMatch}\\s*$`, 'gm'), '')
          .replace(/\n\n+/g, '\n')
          .replace(/^\n+|\n+$/g, '')
          .trim()
      } else {
        // Fallback: Try to extract /api/images/ URL manually
        if (message.messageText.includes('/api/images/') || message.messageText.includes('api/images/')) {
          const manualMatch = message.messageText.match(/(\/api\/images\/[a-fA-F0-9]+|api\/images\/[a-fA-F0-9]+)/)
          if (manualMatch) {
            let path = manualMatch[0]
            if (!path.startsWith('/')) {
              path = '/' + path
            }
            imagePath = path
            
            const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
            imageUrl = path.startsWith('http') 
              ? path 
              : path.startsWith('/')
              ? `${baseUrl}${path}`
              : `${baseUrl}/${path}`
            
            const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            textWithoutImage = message.messageText
              .replace(new RegExp(`\\s*${escapedPath}\\s*`, 'g'), '')
              .replace(new RegExp(`^\\s*${escapedPath}\\s*$`, 'gm'), '')
              .replace(/\n\n+/g, '\n')
              .replace(/^\n+|\n+$/g, '')
              .trim()
          }
        }
        
        // Legacy: Try to match [IMAGE:...] for base64 images
        if (!imageUrl) {
          const imageMatch = message.messageText.match(/\[IMAGE:([\s\S]*?)\]/)
          if (imageMatch && imageMatch[1]) {
            const extracted = imageMatch[1].trim()
            imagePath = extracted // Use full base64 string as path/content
            
            if (extracted.startsWith('data:image/')) {
              imageUrl = extracted
            } else if (extracted.startsWith('data:')) {
              imageUrl = extracted
            } else {
              let mimeType = 'image/jpeg'
              if (extracted.startsWith('/9j/') || extracted.startsWith('iVBORw0KGgo')) {
                mimeType = extracted.startsWith('/9j/') ? 'image/jpeg' : 'image/png'
              }
              imageUrl = `data:${mimeType};base64,${extracted}`
            }
            
            textWithoutImage = message.messageText.replace(/\[IMAGE:[\s\S]*?\]/, '').trim()
          }
        }
      }
    }
    
    setEditingMessageText(textWithoutImage)
    setEditingMessageImageUrl(imageUrl)
    setEditingMessageImagePath(imagePath)
  }

  // Handle edit image selection
  const handleEditImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setEditingNewImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setEditingNewImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle remove edit image
  const handleRemoveEditImage = () => {
    setEditingNewImage(null)
    setEditingNewImagePreview(null)
  }

  // Handle save edited message
  const handleSaveEdit = async (messageId: string) => {
    if (!editingMessageText.trim() && !editingMessageImageUrl && !editingNewImage) {
      toast({
        title: 'Error',
        description: 'Message cannot be empty',
        variant: 'destructive',
      })
      return
    }

    try {
      let finalMessageText = editingMessageText.trim()
      
      // If a new image is selected, upload it and use it (replaces old image)
      if (editingNewImage) {
        try {
          const base64Image = await imageToBase64(editingNewImage)
          if (finalMessageText) {
            finalMessageText = `${finalMessageText}\n[IMAGE:${base64Image}]`
          } else {
            finalMessageText = `[IMAGE:${base64Image}]`
          }
        } catch (error) {
          console.error('Failed to convert image:', error)
          toast({
            title: 'Error',
            description: 'Failed to process image',
            variant: 'destructive',
          })
          return
        }
      } else if (editingMessageImageUrl) {
        // If no new image, preserve the old image
        if (editingMessageImageUrl.startsWith('data:')) {
          finalMessageText = finalMessageText 
            ? `${finalMessageText}\n[IMAGE:${editingMessageImageUrl}]`
            : `[IMAGE:${editingMessageImageUrl}]`
        } else if (editingMessageImagePath) {
          // Use the original path/content we stored
          // Check if it's base64 without prefix (legacy)
          if (!editingMessageImagePath.startsWith('/') && !editingMessageImagePath.startsWith('http')) {
             finalMessageText = finalMessageText 
              ? `${finalMessageText}\n[IMAGE:${editingMessageImagePath}]`
              : `[IMAGE:${editingMessageImagePath}]`
          } else {
             finalMessageText = finalMessageText 
              ? `${finalMessageText}\n${editingMessageImagePath}`
              : editingMessageImagePath
          }
        } else {
          // Fallback
          finalMessageText = finalMessageText 
            ? `${finalMessageText}\n${editingMessageImageUrl}`
            : editingMessageImageUrl
        }
      }

      await apiService.updateGuestMessage(messageId, finalMessageText)
      toast({
        title: 'Success',
        description: 'Message updated successfully',
      })
      
      // Reload messages
      if (isAdminOrReceptionist && selectedGuestForChat) {
        await loadChatMessages(selectedGuestForChat)
      } else {
        await loadMessages()
      }
      
      setEditingMessageId(null)
      setEditingMessageText('')
      setEditingMessageImageUrl(null)
      setEditingMessageImagePath(null)
      setEditingNewImage(null)
      setEditingNewImagePreview(null)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update message',
        variant: 'destructive',
      })
    }
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingMessageText('')
    setEditingMessageImageUrl(null)
    setEditingMessageImagePath(null)
    setEditingNewImage(null)
    setEditingNewImagePreview(null)
  }

  // Handle delete message
  const handleDeleteMessage = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return
    }

    setDeletingMessageId(messageId)
    try {
      await apiService.deleteGuestMessage(messageId)
      toast({
        title: 'Success',
        description: 'Message deleted successfully',
      })
      
      // Reload messages
      if (isAdminOrReceptionist && selectedGuestForChat) {
        await loadChatMessages(selectedGuestForChat)
      } else {
        await loadMessages()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete message',
        variant: 'destructive',
      })
    } finally {
      setDeletingMessageId(null)
    }
  }

  // Check if user can edit/delete a message
  const canEditDeleteMessage = (message: any) => {
    // If admin/receptionist, can edit ONLY staff messages (not guest messages)
    if (isAdminOrReceptionist) {
      // Allow editing if it's NOT from guest (staff message)
      // And ideally if it matches their user ID, but generally staff can edit staff messages
      // "messages not send by others" - assuming this means "not sent by guest" OR "only my messages"
      // If strict ownership is required: return !message.isFromGuest && message.userId === user?.id
      // For now, assuming staff can edit their own messages:
      return !message.isFromGuest && (message.userId === user?.id || message.user?.id === user?.id)
    }
    
    // If guest, can edit ONLY guest messages (their own)
    if (message.isFromGuest) return true
    
    return false
  }

  if (!user) return null

  return (
    <>
      {/* Fixed Messages Button */}
      <div className="fixed right-2 bottom-32 z-[9998] pointer-events-auto">
        <Button
          onClick={() => {
            setShowMessages(!showMessages)
            if (!showMessages) {
              if (isAdminOrReceptionist) {
                loadGuestsWithMessages()
              } else {
                loadMessages()
              }
            }
          }}
          className="rounded-full h-14 w-14 shadow-lg hover:shadow-xl transition-all"
          style={{ backgroundColor: '#6467f2' }}
        >
          <MessageSquare className="h-6 w-6 text-white" />
          {unreadMessageCount > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-red-500 text-white border-2 border-white h-6 w-6 flex items-center justify-center p-0 text-xs">
              {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Messages Sidebar - Overlay (Fixed Position) */}
      {showMessages && (
        <div className="fixed right-4 top-1/2 -translate-y-1/2 z-[100] w-96 animate-in slide-in-from-right duration-300">
          <Card className="border-0 shadow-2xl">
            <CardHeader className="border-b" style={{ 
              background: 'linear-gradient(135deg, #6467f2 0%, #6467f2 100%)',
            }}>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-white">
                  <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                    <MessageSquare className="h-5 w-5" />
                  </div>
                  <span>Messages</span>
                </CardTitle>
                <Button
                  onClick={() => setShowMessages(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription className="text-white/90 mt-2">
                {isAdminOrReceptionist ? 'Chat with guests' : 'Chat with hotel staff'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 bg-white p-0 flex flex-col" style={{ height: '500px', maxHeight: '80vh' }}>
              {isAdminOrReceptionist ? (
                // Admin/Receptionist View - Show guests list and chat
                <div className="flex flex-col h-full">
                  {/* Guests List - Hide when chat is open */}
                  {!selectedGuestForChat && (
                    <div className="border-b flex-1 overflow-y-auto">
                    {loadingGuestsWithMessages ? (
                      <div className="flex items-center justify-center py-8">
                        <MoonLoader />
                      </div>
                    ) : guestsWithMessages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mb-2 opacity-50" />
                        <p className="text-sm">No messages yet</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {guestsWithMessages.map((guest) => (
                          <button
                            key={guest.id}
                            onClick={() => {
                              setSelectedGuestForChat(guest.id)
                              loadChatMessages(guest.id)
                            }}
                            className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                              selectedGuestForChat === guest.id ? 'bg-muted' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {guest.firstName} {guest.lastName}
                                </p>
                                {guest.lastMessage && (() => {
                                  const { hasImage, text } = extractImageAndText(guest.lastMessage.text)
                                  return (
                                    <p className="text-sm text-muted-foreground truncate mt-1">
                                      {hasImage && <span className="inline-flex items-center gap-1 mr-1">📷 Image</span>}
                                      {text || (hasImage ? '' : guest.lastMessage.text)}
                                    </p>
                                  )
                                })()}
                              </div>
                              {guest.unreadCount > 0 && (
                                <Badge className="bg-red-500 text-white">
                                  {guest.unreadCount > 99 ? '99+' : guest.unreadCount}
                                </Badge>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    </div>
                  )}

                  {/* Chat Area */}
                  <div className={`flex flex-col h-full ${selectedGuestForChat ? 'flex-1 min-h-0' : 'flex-1 border-t'}`}>
                    {selectedGuestForChat ? (
                      <>
                        {/* Chat Header */}
                        <div className="p-3 border-b bg-muted/50">
                          {(() => {
                            const guest = guestsWithMessages.find(g => g.id === selectedGuestForChat)
                            return guest ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  onClick={() => {
                                    setSelectedGuestForChat(null)
                                    setMessages([]) // Clear messages when going back
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-muted"
                                  title="Back to guest list"
                                >
                                  <ArrowLeft className="h-4 w-4" />
                                </Button>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-sm truncate">
                                    {guest.firstName} {guest.lastName}
                                  </h3>
                                  {guest.email && (
                                    <p className="text-xs text-muted-foreground truncate">{guest.email}</p>
                                  )}
                                </div>
                              </div>
                            ) : null
                          })()}
                        </div>

                        {/* Messages */}
                        <div 
                          className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='whatsapp' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h100v100H0z' fill='%23e5ddd5'/%3E%3Cpath d='M0 0h50v50H0z' fill='%23efeae6'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23whatsapp)'/%3E%3C/svg%3E")`,
                            backgroundSize: '100px 100px',
                            backgroundRepeat: 'repeat'
                          }}
                        >
                          {loadingMessages ? (
                            <div className="flex items-center justify-center py-8">
                              <MoonLoader />
                            </div>
                          ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                              <MessageSquare className="h-12 w-12 mb-2 opacity-50" />
                              <p className="text-sm">No messages yet</p>
                            </div>
                          ) : (
                            messages.map((message) => {
                              // Extract image URL - check for /api/images/ pattern (MongoDB GridFS) or [IMAGE:...] (legacy base64)
                              let imageUrl: string | null = null
                              let textWithoutImage = message.messageText || ''
                              
                              if (message.messageText) {
                                // First, check for MongoDB GridFS image URL pattern: /api/images/{fileId}
                                // Try multiple regex patterns to catch different formats
                                const patterns = [
                                  /\/api\/images\/([a-fA-F0-9]{24})/,  // Standard format with 24 hex chars
                                  /\/api\/images\/([a-fA-F0-9]+)/,     // Any hex length with leading slash
                                  /api\/images\/([a-fA-F0-9]{24})/,    // Without leading slash, 24 chars
                                  /api\/images\/([a-fA-F0-9]+)/,       // Without leading slash, any length
                                ]
                                
                                let gridFsMatch = null
                                for (const pattern of patterns) {
                                  gridFsMatch = message.messageText.match(pattern)
                                  if (gridFsMatch) break
                                }
                                
                                if (gridFsMatch) {
                                  // Construct full URL like room images (matching pattern from hotel.tsx)
                                  let imagePath = gridFsMatch[0]
                                  // Ensure it starts with / if it doesn't
                                  if (!imagePath.startsWith('/')) {
                                    imagePath = '/' + imagePath
                                  }
                                  
                                  // Get baseUrl from apiService to match other image loading patterns
                                  const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
                                  imageUrl = imagePath.startsWith('http') 
                                    ? imagePath 
                                    : imagePath.startsWith('/')
                                    ? `${baseUrl}${imagePath}`
                                    : `${baseUrl}/${imagePath}`
                                  // Remove the image URL from text (with optional whitespace/newlines around it)
                                  textWithoutImage = message.messageText
                                    .replace(/\s*\/api\/images\/[a-fA-F0-9]+\s*/g, '') // Remove URL with surrounding whitespace
                                    .replace(/^\s*\/api\/images\/[a-fA-F0-9]+\s*$/gm, '') // Remove URL on its own line
                                    .replace(/\s*api\/images\/[a-fA-F0-9]+\s*/g, '') // Remove URL without leading slash
                                    .replace(/\n\n+/g, '\n') // Replace multiple newlines with single
                                    .replace(/^\n+|\n+$/g, '') // Remove leading/trailing newlines
                                    .trim()
                                } else {
                                  // Legacy: Try to match [IMAGE:...] for base64 images (backward compatibility)
                                  const imageMatch = message.messageText.match(/\[IMAGE:([\s\S]*?)\]/)
                                  if (imageMatch && imageMatch[1]) {
                                    const extracted = imageMatch[1].trim()
                                    
                                    // Check if it's already a data URL
                                    if (extracted.startsWith('data:image/')) {
                                      imageUrl = extracted
                                    } else if (extracted.startsWith('data:')) {
                                      imageUrl = extracted
                                    } else {
                                      // Assume it's base64 and add data URL prefix
                                      let mimeType = 'image/jpeg' // default
                                      if (extracted.startsWith('/9j/') || extracted.startsWith('iVBORw0KGgo')) {
                                        mimeType = extracted.startsWith('/9j/') ? 'image/jpeg' : 'image/png'
                                      }
                                      imageUrl = `data:${mimeType};base64,${extracted}`
                                    }
                                    
                                    // Remove the image tag from text
                                    textWithoutImage = message.messageText.replace(/\[IMAGE:[\s\S]*?\]/, '').trim()
                                  }
                                }
                              }
                              
                              const isEditing = editingMessageId === message.id
                              const canEditDelete = canEditDeleteMessage(message)
                              
                              return (
                                <div
                                  key={message.id}
                                  className={`flex ${message.isFromGuest ? 'justify-start' : 'justify-end'}`}
                                >
                                  <div className="relative group max-w-[85%]">
                                    <div
                                      className={`rounded-lg p-3 ${
                                        message.isFromGuest
                                          ? 'bg-white border border-gray-200 text-gray-900'
                                          : 'bg-primary text-white'
                                      }`}
                                      style={
                                        !message.isFromGuest
                                          ? { backgroundColor: '#6467f2' }
                                          : {}
                                      }
                                    >
                                      <div className="flex items-start justify-between gap-2 mb-1">
                                        <p className="text-xs font-medium opacity-80">
                                          {message.isFromGuest
                                            ? `${message.guest?.firstName || ''} ${message.guest?.lastName || ''}`
                                            : message.user?.name || 'You'}
                                        </p>
                                        {canEditDelete && !isEditing && (
                                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                              onClick={() => handleEditMessage(message)}
                                              variant="ghost"
                                              size="sm"
                                              className="h-6 w-6 p-0 hover:bg-black/10"
                                              title="Edit message"
                                            >
                                              <Edit className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              onClick={() => handleDeleteMessage(message.id)}
                                              variant="ghost"
                                              size="sm"
                                              className="h-6 w-6 p-0 hover:bg-red-500/20 text-red-600"
                                              title="Delete message"
                                              disabled={deletingMessageId === message.id}
                                            >
                                              {deletingMessageId === message.id ? (
                                                <MoonLoader size="sm" />
                                              ) : (
                                                <Trash2 className="h-3 w-3" />
                                              )}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {isEditing ? (
                                        <div className="space-y-2">
                                          {/* Show current image or new image preview */}
                                          {editingNewImagePreview ? (
                                            <div className="mb-2 relative">
                                              <p className="text-xs opacity-70 mb-1">New image:</p>
                                              <img 
                                                src={editingNewImagePreview} 
                                                alt="New attachment" 
                                                className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                              />
                                              <Button
                                                onClick={handleRemoveEditImage}
                                                variant="ghost"
                                                size="sm"
                                                className="absolute top-6 right-2 h-6 w-6 p-0 bg-red-500/80 hover:bg-red-500 text-white"
                                                title="Remove new image"
                                              >
                                                <X className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ) : editingMessageImageUrl && (
                                            <div className="mb-2 relative">
                                              <p className="text-xs opacity-70 mb-1">Current image:</p>
                                              <img 
                                                src={editingMessageImageUrl} 
                                                alt="Message attachment" 
                                                className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = 'none'
                                                }}
                                              />
                                            </div>
                                          )}
                                          
                                          {/* Image upload input */}
                                          <div className="mb-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                              <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleEditImageSelect}
                                                className="hidden"
                                                id={`edit-image-${message.id}`}
                                              />
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-8 text-gray-800"
                                                onClick={() => document.getElementById(`edit-image-${message.id}`)?.click()}
                                              >
                                                <ImageIcon className="h-4 w-4 mr-1" />
                                                {editingNewImage ? 'Change Image' : editingMessageImageUrl ? 'Replace Image' : 'Add Image'}
                                              </Button>
                                            </label>
                                          </div>

                                        <Textarea
                                          value={editingMessageText}
                                          onChange={(e) => setEditingMessageText(e.target.value)}
                                          className="min-h-[80px] resize-none text-black placeholder:text-gray-500 bg-white"
                                          autoFocus
                                          placeholder="Type your message..."
                                        />
                                          <div className="flex gap-2 justify-end">
                                            <Button
                                              onClick={handleCancelEdit}
                                              variant="ghost"
                                              size="sm"
                                              className="h-7"
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              onClick={() => handleSaveEdit(message.id)}
                                              size="sm"
                                              className="h-7"
                                              style={{ backgroundColor: '#6467f2' }}
                                            >
                                              Save
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          {imageUrl && (
                                            <div className="mb-2">
                                              <img 
                                                src={imageUrl} 
                                                alt="Message attachment" 
                                                className="max-w-full max-h-[300px] rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                                onError={(e) => {
                                                  // Hide broken image icon
                                                  e.currentTarget.style.display = 'none'
                                                }}
                                                onLoad={(e) => {
                                                  // Ensure image is visible when it loads successfully
                                                  e.currentTarget.style.display = 'block'
                                                }}
                                                onClick={() => {
                                                  const newWindow = window.open()
                                                  if (newWindow) {
                                                    newWindow.document.write(`<img src="${imageUrl}" style="max-width: 100%; height: auto;" />`)
                                                  }
                                                }}
                                              />
                                            </div>
                                          )}
                                          {textWithoutImage && textWithoutImage.trim() && (
                                            <p className="text-sm whitespace-pre-wrap break-words">
                                              {textWithoutImage}
                                            </p>
                                          )}
                                          <p className="text-xs mt-2 opacity-70">
                                            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })
                          )}
                        </div>

                        {/* Message Input */}
                        <div className="p-3 border-t bg-white flex-shrink-0">
                          {imagePreview && (
                            <div className="mb-2 relative inline-block">
                              <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="max-w-[150px] max-h-[150px] rounded-lg border-2 border-gray-200"
                              />
                              <Button
                                onClick={handleRemoveImage}
                                variant="destructive"
                                size="sm"
                                className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="hidden"
                              id="image-upload-admin"
                              disabled={sendingMessage}
                            />
                            <label htmlFor="image-upload-admin">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="px-2 self-end"
                                disabled={sendingMessage}
                                asChild
                              >
                                <span>
                                  <Paperclip className="h-4 w-4" />
                                </span>
                              </Button>
                            </label>
                            <Textarea
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              placeholder="Type your message..."
                              className="flex-1 min-h-[50px] resize-none text-sm text-black placeholder:text-gray-500"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault()
                                  handleSendMessage()
                                }
                              }}
                              disabled={sendingMessage}
                            />
                            <Button
                              onClick={handleSendMessage}
                              disabled={(!newMessage.trim() && !selectedImage) || sendingMessage}
                              className="px-3 self-end"
                              style={{ backgroundColor: '#6467f2' }}
                            >
                              {sendingMessage ? (
                                <MoonLoader />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                        <div>
                          <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Select a guest to start chatting</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Guest View - Show messages directly
                loadingMessages ? (
                  <div className="flex items-center justify-center py-12 flex-1">
                    <MoonLoader />
                  </div>
                ) : (
                  <>
                    {/* Messages List */}
                    <div 
                      className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='whatsapp' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h100v100H0z' fill='%23e5ddd5'/%3E%3Cpath d='M0 0h50v50H0z' fill='%23efeae6'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23whatsapp)'/%3E%3C/svg%3E")`,
                        backgroundSize: '100px 100px',
                        backgroundRepeat: 'repeat'
                      }}
                    >
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                          <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-gray-500 font-medium">No messages yet</p>
                          <p className="text-sm text-gray-400 mt-2">Start a conversation</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message) => {
                            // Extract image URL - check for /api/images/ pattern (MongoDB GridFS) or [IMAGE:...] (legacy base64)
                            let imageUrl: string | null = null
                            let textWithoutImage = message.messageText || ''
                            
                            if (message.messageText) {
                              // First, check for MongoDB GridFS image URL pattern: /api/images/{fileId}
                              // Try multiple regex patterns to catch different formats
                              const patterns = [
                                /\/api\/images\/([a-fA-F0-9]{24})/,  // Standard format with 24 hex chars
                                /\/api\/images\/([a-fA-F0-9]+)/,     // Any hex length with leading slash
                                /api\/images\/([a-fA-F0-9]{24})/,    // Without leading slash, 24 chars
                                /api\/images\/([a-fA-F0-9]+)/,       // Without leading slash, any length
                              ]
                              
                              let gridFsMatch = null
                              let matchedPattern = null
                              for (const pattern of patterns) {
                                gridFsMatch = message.messageText.match(pattern)
                                if (gridFsMatch) {
                                  matchedPattern = pattern
                                  break
                                }
                              }
                              
                              if (gridFsMatch) {
                                // Construct full URL like room images (matching pattern from hotel.tsx)
                                let imagePath = gridFsMatch[0]
                                // Ensure it starts with / if it doesn't
                                if (!imagePath.startsWith('/')) {
                                  imagePath = '/' + imagePath
                                }
                                
                                // Get baseUrl from apiService to match other image loading patterns
                                const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
                                imageUrl = imagePath.startsWith('http') 
                                  ? imagePath 
                                  : imagePath.startsWith('/')
                                  ? `${baseUrl}${imagePath}`
                                  : `${baseUrl}/${imagePath}`
                                // Remove the image URL from text (escape special regex characters in the matched string)
                                const escapedMatch = gridFsMatch[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                textWithoutImage = message.messageText
                                  .replace(new RegExp(`\\s*${escapedMatch}\\s*`, 'g'), '') // Remove matched URL with surrounding whitespace
                                  .replace(new RegExp(`^\\s*${escapedMatch}\\s*$`, 'gm'), '') // Remove URL on its own line
                                  .replace(/\n\n+/g, '\n') // Replace multiple newlines with single
                                  .replace(/^\n+|\n+$/g, '') // Remove leading/trailing newlines
                                  .trim()
                              } else {
                                // Fallback: Try to extract /api/images/ URL manually if pattern didn't match
                                if (message.messageText.includes('/api/images/') || message.messageText.includes('api/images/')) {
                                  // Try to extract manually
                                  const manualMatch = message.messageText.match(/(\/api\/images\/[a-fA-F0-9]+|api\/images\/[a-fA-F0-9]+)/)
                                  if (manualMatch) {
                                    let imagePath = manualMatch[0]
                                    if (!imagePath.startsWith('/')) {
                                      imagePath = '/' + imagePath
                                    }
                                    
                                    const baseUrl = (apiService as any)['baseUrl'] || config.api.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')
                                    imageUrl = imagePath.startsWith('http') 
                                      ? imagePath 
                                      : imagePath.startsWith('/')
                                      ? `${baseUrl}${imagePath}`
                                      : `${baseUrl}/${imagePath}`
                                    
                                    // Remove the image URL from text
                                    const escapedPath = imagePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                    textWithoutImage = message.messageText
                                      .replace(new RegExp(`\\s*${escapedPath}\\s*`, 'g'), '')
                                      .replace(new RegExp(`^\\s*${escapedPath}\\s*$`, 'gm'), '')
                                      .replace(/\n\n+/g, '\n')
                                      .replace(/^\n+|\n+$/g, '')
                                      .trim()
                                  }
                                }
                                
                                // Legacy: Try to match [IMAGE:...] for base64 images (backward compatibility)
                                if (!imageUrl) {
                                  const imageMatch = message.messageText.match(/\[IMAGE:([\s\S]*?)\]/)
                                  if (imageMatch && imageMatch[1]) {
                                    const extracted = imageMatch[1].trim()
                                    
                                    // Check if it's already a data URL
                                    if (extracted.startsWith('data:image/')) {
                                      imageUrl = extracted
                                    } else if (extracted.startsWith('data:')) {
                                      imageUrl = extracted
                                    } else {
                                      // Assume it's base64 and add data URL prefix
                                      let mimeType = 'image/jpeg' // default
                                      if (extracted.startsWith('/9j/') || extracted.startsWith('iVBORw0KGgo')) {
                                        mimeType = extracted.startsWith('/9j/') ? 'image/jpeg' : 'image/png'
                                      }
                                      imageUrl = `data:${mimeType};base64,${extracted}`
                                    }
                                    
                                    // Remove the image tag from text
                                    textWithoutImage = message.messageText.replace(/\[IMAGE:[\s\S]*?\]/, '').trim()
                                  }
                                }
                              }
                            }
                            
                            const isEditing = editingMessageId === message.id
                            const canEditDelete = canEditDeleteMessage(message)
                            
                            return (
                              <div
                                key={message.id}
                                className={`flex ${message.isFromGuest ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className="relative group max-w-[85%]">
                                  <div
                                    className={`rounded-lg p-3 ${
                                      message.isFromGuest
                                        ? 'bg-primary text-white'
                                        : 'bg-white border border-gray-200 text-gray-900'
                                    }`}
                                    style={
                                      message.isFromGuest
                                        ? { backgroundColor: '#6467f2' }
                                        : {}
                                    }
                                  >
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <p className="text-xs font-medium opacity-80">
                                        {message.isFromGuest
                                          ? 'You'
                                          : message.user?.name || 'Hotel Staff'}
                                      </p>
                                      {canEditDelete && !isEditing && (
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Button
                                            onClick={() => handleEditMessage(message)}
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 hover:bg-black/10"
                                            title="Edit message"
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            onClick={() => handleDeleteMessage(message.id)}
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 hover:bg-red-500/20 text-red-600"
                                            title="Delete message"
                                            disabled={deletingMessageId === message.id}
                                          >
                                            {deletingMessageId === message.id ? (
                                              <MoonLoader size="sm" />
                                            ) : (
                                              <Trash2 className="h-3 w-3" />
                                            )}
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {isEditing ? (
                                      <div className="space-y-2">
                                        {/* Show current image or new image preview */}
                                        {editingNewImagePreview ? (
                                          <div className="mb-2 relative">
                                            <p className="text-xs opacity-70 mb-1">New image:</p>
                                            <img 
                                              src={editingNewImagePreview} 
                                              alt="New attachment" 
                                              className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                            />
                                            <Button
                                              onClick={handleRemoveEditImage}
                                              variant="ghost"
                                              size="sm"
                                              className="absolute top-6 right-2 h-6 w-6 p-0 bg-red-500/80 hover:bg-red-500 text-white"
                                              title="Remove new image"
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ) : editingMessageImageUrl && (
                                          <div className="mb-2 relative">
                                            <p className="text-xs opacity-70 mb-1">Current image:</p>
                                            <img 
                                              src={editingMessageImageUrl} 
                                              alt="Message attachment" 
                                              className="max-w-full max-h-[200px] rounded-lg object-contain border-2 border-gray-200"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none'
                                              }}
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Image upload input */}
                                        <div className="mb-2">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={handleEditImageSelect}
                                              className="hidden"
                                              id={`edit-image-${message.id}`}
                                            />
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              className="h-8 text-gray-800"
                                              onClick={() => document.getElementById(`edit-image-${message.id}`)?.click()}
                                            >
                                              <ImageIcon className="h-4 w-4 mr-1" />
                                              {editingNewImage ? 'Change Image' : editingMessageImageUrl ? 'Replace Image' : 'Add Image'}
                                            </Button>
                                          </label>
                                        </div>

                                        <Textarea
                                          value={editingMessageText}
                                          onChange={(e) => setEditingMessageText(e.target.value)}
                                          className="min-h-[80px] resize-none text-black"
                                          autoFocus
                                          placeholder="Type your message..."
                                        />
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            onClick={handleCancelEdit}
                                            variant="ghost"
                                            size="sm"
                                            className="h-7"
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            onClick={() => handleSaveEdit(message.id)}
                                            size="sm"
                                            className="h-7"
                                            style={{ backgroundColor: '#6467f2' }}
                                          >
                                            Save
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {imageUrl && (
                                          <div className="mb-2">
                                            <img 
                                              src={imageUrl} 
                                              alt="Message attachment" 
                                              className="max-w-full max-h-[300px] rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                              onError={(e) => {
                                                // Hide broken image icon
                                                e.currentTarget.style.display = 'none'
                                              }}
                                              onLoad={(e) => {
                                                // Ensure image is visible when it loads successfully
                                                e.currentTarget.style.display = 'block'
                                              }}
                                              onClick={() => {
                                                const newWindow = window.open()
                                                if (newWindow) {
                                                  newWindow.document.write(`<img src="${imageUrl}" style="max-width: 100%; height: auto;" />`)
                                                }
                                              }}
                                            />
                                          </div>
                                        )}
                                        {textWithoutImage && textWithoutImage.trim() && (
                                          <p className="text-sm whitespace-pre-wrap break-words">
                                            {textWithoutImage}
                                          </p>
                                        )}
                                        <p className="text-xs mt-2 opacity-70">
                                          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t bg-white flex-shrink-0">
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="mb-2 relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="max-w-[200px] max-h-[200px] rounded-lg border-2 border-gray-200"
                          />
                          <Button
                            onClick={handleRemoveImage}
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="image-upload-global"
                          disabled={sendingMessage}
                        />
                        <label htmlFor="image-upload-global">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="px-3 self-end"
                            disabled={sendingMessage}
                            asChild
                          >
                            <span>
                              <Paperclip className="h-4 w-4" />
                            </span>
                          </Button>
                        </label>
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 min-h-[60px] resize-none text-sm text-black"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleSendMessage()
                            }
                          }}
                          disabled={sendingMessage}
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={(!newMessage.trim() && !selectedImage) || sendingMessage}
                          className="px-4 self-end"
                          style={{ backgroundColor: '#6467f2' }}
                        >
                          {sendingMessage ? (
                            <MoonLoader />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}

